import { IMat4, Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { ISize2, Size2 } from "@base/math/Size2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { CacheUtil } from "@base/util/Cache";
import { eqc, gtc, lec } from "@base/util/Comma";
import { getKeys } from "@base/util/TypeUtil";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { Debug } from "@biz/Debug";
import { RAFF_PREMONTAGE } from "@biz/definitions/ArticleDefinitions";
import { FL } from "@biz/definitions/FL";
import { RemoteChannelCount } from "@biz/definitions/MountDefinitons";
import { FeatureLevel, ShipmentType } from "@biz/definitions/ProductDefinitions";
import { ProductVersion, Pvi } from "@biz/definitions/ProductVersion";
import { Auto, ClothType, RemoteType } from "@biz/definitions/SunSailDefinitions";
import {
  IRaffData, IRaffFieldCalculation, PergolaMaterial, RaffFieldCalculationType, RaffFieldCutLayout, RaffInstallationLocation, RaffPulleySystem, raffPulleySystems, RaffRollType, RaffSailMountType, RaffType, RopeTensioningLengthType,
    RopeTensioningSystemType
} from "@biz/items/raff/RaffDefinitions";
import { ClothData, IClothData } from "@biz/logic/ClothData";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { getChecked } from "@biz/logic/getChecked";
import { WishList } from "@biz/models/ShoppingList";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ISCRaffData } from "@biz/sc/raff/ISCRaffData";
import { getRollWidth } from "@biz/SunSailColors";

interface IGuideConfig {
  maxGuideWidth: number;
  maxOverhang: number;
}

interface IFieldCalculationConfig {
  target: number;
  minFieldWidth: number;
  maxFieldWidth: number;
  minPanelCount: number;
}
export interface IRaffGuideInfo {
  guideCount: number;
  overhangLeft: number;
  overhangRight: number;
  guideWidth: number;
}

export interface IRaffFieldInfo {
  fieldStart: number;
  fieldCount: number;
  fieldWidth: number;
  fieldWidthCloth: number;
  minFields: number;
  maxFields: number;
}

export class RaffLogic {

  static getCloth(raff: IRaffData | ISCRaffData): IClothData | undefined {
    return raff.cloth; // ClothDataLogic.getCloth(raff.cloth, PD.productToPD(raff), FL.fromProduct(raff));
  }

  static getDefaultCloth(pd: IProductDescriptor, featureLevel: FeatureLevel, raffWidth: number, raffLength: number): IClothData | undefined {
    const clothDatas = ClothDataLogic.calcValidClothDatasArray(pd, featureLevel);
    for (const clothData of clothDatas) {

      if  (RaffLogic.calcMaxClothWidthSize(clothData).w >= raffWidth && RaffLogic.calcMaxClothWidthSize(clothData).h >= raffLength) {
        return clothData;
      }
    }

    return undefined;
  }

  static hasMotor(raffType: RaffType): boolean {
    return raffType === RaffType.C;
  }
  static getMotorRemoteType(product: IRaffData): RemoteType | undefined {
    if (!RaffLogic.hasMotor(product.raffType))
      return undefined;
    if (product.articleSlots.remoteReceiver) {
      switch (product.articleSlots.remoteReceiver) {
        case "00E13001384": return "centronic";
        case "00E13001972": return "centronic+";
        case "00E13001924": return "centronic+";
      }
      return undefined;
    }
    if (WishList.hasArticle(product.shoppingList.items, "00E13001384", { poleIndexFilter: -1 })) // depreacated => old
      return "centronic";
    return undefined;
  }

  static calcRemoteChannelCount(product: IRaffData): RemoteChannelCount {
    const heightChangeChannels = 0;
    const motorRemoteType = RaffLogic.getMotorRemoteType(product);
    const motorChannels = motorRemoteType ? 1 : 0;
    return {
      motorChannels,
      totalChannels: motorChannels + heightChangeChannels,
      sunWindSensorCount: 0,
      motorRemoteType
    }
  }

  static getRaffSize(raff: IRaffData | ISCRaffData, options?: { skipTensioningSystem?: boolean }): ISize2 {
    const min = RaffLogic.calcMinRaffSize(raff);
    const max = RaffLogic.calcMaxRaffSize(raff, options);
    return {
      w: MathUtil.clamp(raff.width || min.w, min.w, max.w),
      h: MathUtil.clamp(raff.length || min.h, min.h, max.h)
    }
  }

  static getRaffWidth(raff: IRaffData | ISCRaffData): number {
    return RaffLogic.getRaffSize(raff).w;
  }

  static getRaffLength(raff: IRaffData | ISCRaffData): number {
    return RaffLogic.getRaffSize(raff).h;
  }

  static getRaffHeight(raff: IRaffData | ISCRaffData): number {
    return raff.height;
  }

  static getGuidesCount(raff: IRaffData): Auto<number> {
    if (raff.guidesCount === "auto")
      return "auto";
    const maxGuides = RaffLogic.calcMaxGuides(raff.raffType, RaffLogic.getRaffWidth(raff))
    return MathUtil.clamp(raff.guidesCount || 0, 2, maxGuides);
  }


  static getGuideSettings(raff: IRaffData): IGuideConfig {
    const { version, raffType } = raff;

    const isOutdoor = RaffLogic.getInstallationLocation(raff.installationLocation) === "outdoor";
    if (Pvi.is(version, "2024")) {
      if (raffType === RaffType.M_Rope) {
        const raffSailMount = RaffLogic.getSailMount(raff);
        if (raffSailMount === "hemstitch") { // Jeremy
          return { maxGuideWidth: 2, maxOverhang: 0 }
        }
        const tensioningSystem = RaffLogic.getRopeTensioningSystem(raff);
        if (tensioningSystem === "basic") { // https://plaspack.atlassian.net/browse/SSK-78
          return { maxGuideWidth: 2, maxOverhang: isOutdoor ? 0.05 : 1.0 }
        }

        return { maxGuideWidth: isOutdoor ? 2 : 3, maxOverhang: isOutdoor ? 1 : 1.5 }
      }
      return { maxGuideWidth: isOutdoor ? 3 : 4, maxOverhang: isOutdoor ? 1 : 1.5 }
    } else {
      // old Raffs
      return { maxGuideWidth: 2.4, maxOverhang: 0.6 }
    }
  }

  static getInstallationLocation(installationLocation: RaffInstallationLocation | undefined): RaffInstallationLocation {
    return installationLocation || "outdoor"
  }

  static getPossibleRopeTensioningLengthTypes(raff: IRaffData | ISCRaffData): RopeTensioningLengthType[] {
    const tensioningSystem = RaffLogic.getRopeTensioningSystem(raff);
    if (tensioningSystem === "premium")
      return ["roundUp", "exact"];
    else if (tensioningSystem === "basic")
      return ["roundUp"];
    return []
  }

  static getRopeTensioningLengthType(raff: IRaffData | ISCRaffData): RopeTensioningLengthType | undefined {
    return getChecked({
      selection: raff.ropeTensioningLengthType,
      validItems: RaffLogic.getPossibleRopeTensioningLengthTypes(raff),
      emptySelection: "__selectionRequired__"
    })!
  }

  static getPossiblePergolaMaterials(): PergolaMaterial[] {
    return ["metal", "wood"];
  }

  static getPergolaMaterial(raff: IRaffData | ISCRaffData): PergolaMaterial {
    return getChecked({
      selection: raff.pergolaMaterial,
      validItems: RaffLogic.getPossiblePergolaMaterials(),
      emptySelection: "__selectionRequired__"
    })!
  }

  static getPossibleSailMounts(raff: IRaffData | ISCRaffData): RaffSailMountType[] {
    if (FL.fromProduct(raff) === "dealer")
      return ["profile"]; // https://plaspack.atlassian.net/browse/APP-1577?linkSource=email
    if (raff.raffType === RaffType.M_Rope)
      return ["profile", "hemstitch"];
    return ["profile"];
  }

  static getSailMount(raff: IRaffData | ISCRaffData): RaffSailMountType | undefined {
    return getChecked({
      selection: raff.sailMount,
      validItems: RaffLogic.getPossibleSailMounts(raff),
      emptySelection: "__selectionRequired__"
    })
  }

  static calcMaxSailMountSize(sailMount: RaffSailMountType | undefined): ISize2 {
    switch (sailMount) {
      case "hemstitch": return { w: 4, h: Number.MAX_VALUE };
      case "profile": return { w: Number.MAX_VALUE, h: Number.MAX_VALUE };
    }
    return { w: Number.MAX_VALUE, h: Number.MAX_VALUE };
  }

  static calcMaxRopeTensioningSize(ropeTensioningSystem: RopeTensioningSystemType | undefined, featureLevel: FeatureLevel): ISize2 {
    switch (ropeTensioningSystem) {
      case "basic":
        return { w: 4, h: 5 }; // https://plaspack.atlassian.net/browse/APP-1579
      case "premium": {
        if (FL.ge(featureLevel, "soliday"))
          return Size2.from(10, 12);
        else if (FL.ge(featureLevel, "dealer"))
          return Size2.from(6, 10);
        return Size2.from(6, 8);
      }
    }
    return { w: Number.MAX_VALUE, h: Number.MAX_VALUE };
  }

  static calcMaxClothWidthSize(cloth: IClothData | undefined): ISize2 {
    if (ClothData.isAustronet(cloth?.clothType))
      return { w: 3.9, h: Number.MAX_VALUE, }
    return { w: Number.MAX_VALUE, h: Number.MAX_VALUE };
  }


  static #calcMaxRaffSize(raff: IRaffData | ISCRaffData, options?: { skipTensioningSystem?: boolean, skipCloth?: boolean }): ISize2 {
    const maxCloth = options?.skipCloth ? Size2.MAX_VALUE : RaffLogic.calcMaxClothWidthSize(RaffLogic.getCloth(raff));
    switch (raff.raffType) {
      case RaffType.C:
        return Size2.min({ w: 10, h: 10 }, maxCloth)
      case RaffType.M_Rope: {
        if (!Pvi.is(raff.version, "2024")) { // old systems
          return Size2.min({ w: 4.5, h: 8 }, maxCloth)
        } else {
          const maxSailMount = RaffLogic.calcMaxSailMountSize(RaffLogic.getSailMount(raff));
          const maxRopeTensioning = options?.skipTensioningSystem ? Size2.MAX_VALUE : RaffLogic.calcMaxRopeTensioningSize(RaffLogic.getRopeTensioningSystem(raff), raff.featureLevel);
          return Size2.min(Size2.from(20, 20), maxCloth, maxSailMount, maxRopeTensioning)
        }
      }
      case RaffType.M_Guide:
        return Size2.min({ w: 8, h: 10 }, maxCloth)
    }
    return Size2.from(3, 3);
  }

  static calcMaxRaffSize = CacheUtil.memo(RaffLogic.#calcMaxRaffSize, "calcMaxRaffSize", {
    idsFromArgs: (raff) => [raff.id],
    inputsEqualFn: "shallow",
    resultEqualFn: "shallow"
  });

  static calcMinRaffSize(raff: IRaffData | ISCRaffData): ISize2 {
    if (raff.version === "2025" && raff.featureLevel === "soliday")
      return Size2.from(0.4, 1);
    return Size2.from(0.6, 1);
  }

  static isLong(length: number): boolean {
    return gtc(length, 6, 2);
  }

  static calcMaxGuides(_raffType: RaffType, width: number): number {
    return 2 + Math.round(width / 2); // https://plaspack.atlassian.net/browse/API-4
    // if (width >= 6)
    //   return 5;

    // switch (raffType) {
    //   case RaffType.M_Guide:
    //     return 4;
    //   case RaffType.M_Rope:
    //     return 3;
    //   case RaffType.C:
    //     return width >= 2.3999 ? 4 : 3;
    // }
    // return 3;
  }

  static isRainCutAvailable(raff: IRaffData | ISCRaffData): boolean {
    if (raff.raffType === RaffType.M_Rope && !Pvi.is(raff.version, "2024"))
      return false;
    if (RaffLogic.getSailMount(raff) === "hemstitch") // https://plaspack.atlassian.net/browse/SSK-76
      return false;
    switch (RaffLogic.getCloth(raff)?.clothType) {
      case ClothType.Austrosail_Acryl:
      case ClothType.Austrosail_Nano:
      case ClothType.Concept:
      case ClothType.SoltisW96:
        return eqc(RaffLogic.getAdditionalMaterialFactor(raff), 0, 2); // https://plaspack.atlassian.net/browse/SSK-30
    }
    return false;
  }

  static hasFieldCalculation2025(buildTime: number): boolean {
    return buildTime >= 2502060822;
    // AdditionalMaterialFactor is now possible for Standard, StandardWide and IndividualFields
  }

  static getAdditionalMaterialFactor(raff: IRaffData | ISCRaffData): number {
    return RaffLogic.getAdditionalMaterialFactorInternal(raff.fieldCalculation, raff.buildTime);
  }

  static getAdditionalMaterialFactorRange(fieldCalculationType: RaffFieldCalculationType): { min: number; max: number } {
    if (fieldCalculationType === RaffFieldCalculationType.IndividualFields)
      return { min: 0, max: 0 }
    return {
      min: 0,
      max: 30
    }
  }

  static getAdditionalMaterialFactorInternal(fieldCalculation: IRaffFieldCalculation, buildTime: number): number {
    if (!RaffLogic.hasFieldCalculation2025(buildTime)) { // old raff projects
      if (fieldCalculation.type !== RaffFieldCalculationType.AdditionalMaterial)
        return 0;
    }
    const factor = fieldCalculation.individualMaterialAdditionFactor || 0;
    const range = RaffLogic.getAdditionalMaterialFactorRange(fieldCalculation.type);
    return MathUtil.clamp(factor, range.min, range.max);
  }

  static getPossibleFieldCalculationTypes(raff: IRaffData | ISCRaffData): RaffFieldCalculationType[] {
    if (raff.featureLevel === "user") {
      return [RaffFieldCalculationType.Standard, RaffFieldCalculationType.StandardWide]
    }

    const ret: RaffFieldCalculationType[] = [
      RaffFieldCalculationType.Standard,
      RaffFieldCalculationType.StandardWide,
      RaffFieldCalculationType.IndividualFields,
    ];
    if (raff.fieldCalculation.type === RaffFieldCalculationType.AdditionalMaterial) // just if it is currently set (e.g. in old projects)
      ret.push(RaffFieldCalculationType.AdditionalMaterial);
    return ret;
  }

  static getFieldCalculationType(raff: IRaffData | ISCRaffData): RaffFieldCalculationType {
    // raff.fieldCalculation.type
    return getChecked({
      selection: raff.fieldCalculation?.type || RaffFieldCalculationType.Standard,
      validItems: RaffLogic.getPossibleFieldCalculationTypes(raff),
      emptySelection: "__selectionRequired__"
    })!
  }

  static getRainCut(raff: IRaffData | ISCRaffData): boolean {
    if (!raff.rainCut)
      return false;
    if (!RaffLogic.isRainCutAvailable(raff))
      return false;
    return true;
  }

  static supportsDistanceIn(raffType: RaffType): boolean {
    return raffType === RaffType.M_Guide || raffType === RaffType.M_Rope;
  }

  static getMaxAngle(_raffType: RaffType, width: number, length: number): number {
    const widthCount = 10;
    const lengthCount = 10;
    const table: number[] = [
      25, 20, 15, 15, 15, 10, 0, 0, 0, 0, // 1
      25, 20, 15, 15, 15, 10, 0, 0, 0, 0,  // 2
      25, 20, 15, 15, 10, 5, 0, 0, 0, 0,  // 3
      25, 20, 15, 10, 10, 5, 0, 0, 0, 0,  // 4
      25, 20, 15, 10, 5, 5, 0, 0, 0, 0,  // 5
      25, 20, 15, 5, 5, 5, 0, 0, 0, 0,  // 6
      25, 20, 15, 5, 5, 0, 0, 0, 0, 0,  // 7
      25, 20, 10, 5, 0, 0, 0, 0, 0, 0,  // 8
      25, 15, 10, 0, 0, 0, 0, 0, 0, 0,  // 9
      25, 15, 0, 0, 0, 0, 0, 0, 0, 0,  // 10
    ]
    Debug.assert(table.length === widthCount * lengthCount, "Invalid nuber of table entries!")
    const iw = MathUtil.clamp(Math.floor(width), 0, widthCount - 1);
    const il = MathUtil.clamp(Math.floor(length), 0, lengthCount - 1);
    return table[il * widthCount + iw];
  }

  static calcUserGuideHelper(guidesCount: Auto<number>, minGuides: number): number {
    // RaffLogic.cpp 538
    if (guidesCount !== "auto")
      return guidesCount;
    const count = MathUtil.clamp(Math.ceil(minGuides) + 1, 2.0, 4.0);
    return count;
  }

  static calcMinGuides(raff: IRaffData): number {
    const { overhang } = raff;
    const raffWidth = RaffLogic.getRaffWidth(raff);

    const { maxOverhang, maxGuideWidth } = RaffLogic.getGuideSettings(raff);
    const left = overhang ? overhang.left : maxOverhang;
    const right = overhang ? overhang.right : maxOverhang;

    const minGuides = Math.max(2, Math.ceil((raffWidth - (left + right)) / maxGuideWidth) + 1);
    return minGuides;
  }

  static calcGuideCount(raff: IRaffData): number {
    const guidesCount = RaffLogic.getGuidesCount(raff);
    if (guidesCount !== "auto")
      return guidesCount;

    return RaffLogic.calcMinGuides(raff);
  }

  static #calcGuideInfo(raff: IRaffData): IRaffGuideInfo {
    const { raffType, overhang } = raff;
    const raffSailMount = RaffLogic.getSailMount(raff);
    const raffWidth = RaffLogic.getRaffWidth(raff);

    const guideCount = RaffLogic.calcGuideCount(raff);
    const { maxGuideWidth, maxOverhang } = RaffLogic.getGuideSettings(raff);

    let overhangLeft: number;
    let overhangRight: number
    if (raffType === RaffType.M_Rope && raffSailMount === "hemstitch") {
      overhangLeft = 0.02;
      overhangRight = 0.02;
    } else if (overhang) {
      overhangLeft = overhang.left;
      overhangRight = overhang.right;
    } /* else if (raff.overhangLeft.enabled) {
      const m = (guideCount - 1) * maxGuideWidth + maxOverhang;
      const factor = (raff.width - raff.overhangLeft.value) / m;
      overhangLeft = raff.overhangLeft.value;
      overhangRight = maxOverhang * factor;
    } else if (raff.overhangRight.enabled) {
      const m = (guideCount - 1) * maxGuideWidth + maxOverhang;
      const factor = (raff.width - raff.overhangRight.value) / m;
      overhangLeft = maxOverhang * factor;
      overhangRight = raff.overhangRight.value;
    } */
    else {
      const m = (guideCount - 1) * maxGuideWidth + maxOverhang * 2;
      const factor = (raffWidth) / m;
      overhangLeft = maxOverhang * factor;
      overhangRight = maxOverhang * factor;
    }
    return {
      overhangLeft,
      overhangRight,
      guideCount,
      guideWidth: (raffWidth - overhangLeft - overhangRight) / (guideCount - 1)
    }
  }

  static calcGuideInfo = CacheUtil.memo(RaffLogic.#calcGuideInfo, "calcGuideInfoInternal", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow"
  })

  static calcGuidesFromGuideInfo(guideInfo: ReturnType<typeof RaffLogic.calcGuideInfo>): number[] {
    const guides: number[] = [];
    let pos = guideInfo.overhangLeft;
    for (let i = 0; i < guideInfo.guideCount; i++) {
      guides.push(pos);
      pos += guideInfo.guideWidth;
    }
    return guides;
  }

  static #calcGuides(raff: IRaffData): number[] {
    const guideInfos = RaffLogic.calcGuideInfo(raff);
    return RaffLogic.calcGuidesFromGuideInfo(guideInfos);
  }

  static calcGuides = CacheUtil.memo(RaffLogic.#calcGuides, "calcGuides", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow"
  });

  static calcFieldPositionsFromFieldInfo(_id: string, fieldInfo: ReturnType<typeof RaffLogic.calcFieldInfo>): number[] {
    const fieldPositions: number[] = [];
    let pos = fieldInfo.fieldStart;
    for (let i = 0; i <= fieldInfo.fieldCount; i++) {
      fieldPositions.push(pos);
      pos += fieldInfo.fieldWidth;
    }
    return fieldPositions;
  }

  static #calcFieldPositions(raff: IRaffData): number[] {
    const fieldInfo = RaffLogic.#calcFieldInfo(raff);
    const _fields = RaffLogic.calcFieldPositionsFromFieldInfo(raff.id, fieldInfo);
    const animFactor = RaffLogic.getAnimationFactor();
    if (animFactor === 1)
      return _fields;
    return _fields.map((f) => f * animFactor)
  }

  static calcFieldPositions = CacheUtil.memo(RaffLogic.#calcFieldPositions, "calcFields", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow",
  });


  static calcMinMaxField2022(sailSize: ISize2, fieldCalculation: IRaffFieldCalculation, rollWidth: number, rainCut: boolean, buildTime: number): { min: number; max: number } {
    const settings = { border: 60, minFieldWidth: 400, maxWidth: 550 };

    let rainCutBorder = 0;
    if (rainCut) {
      const rainCutAngle = 0.5;
      rainCutBorder = 1000 * sailSize.w * Math.tan(MathUtil.deg2rad(rainCutAngle));
    }
    const fieldCount = Math.floor((rollWidth - rainCutBorder) / (settings.minFieldWidth + settings.border + rainCutBorder))
    const result = (rollWidth - rainCutBorder) / fieldCount - settings.border - rainCutBorder

    // round
    const rounded = Math.floor(result / 5) * 5;
    const maxLength = Math.min(settings.maxWidth, rounded) / 10;

    // RaffLogic.418
    const clothLength = sailSize.h + sailSize.h * RaffLogic.getAdditionalMaterialFactorInternal(fieldCalculation, buildTime);
    const minFields = clothLength * 100 / maxLength;
    const minLength = 30;
    const maxFields = clothLength * 100 / minLength;
    return {
      min: Math.ceil(minFields),
      max: Math.floor(maxFields)
    }
  }

  static calcMaxFieldLength(clothWidth: number, fieldCalculation: IRaffFieldCalculation, cutLayout: RaffFieldCutLayout, rollWidth: number, rainCut: boolean): number {
    const config: IFieldCalculationConfig = fieldCalculation.type === RaffFieldCalculationType.Standard
      ? { target: 600, minFieldWidth: 420, maxFieldWidth: 625, minPanelCount: 2 }
      : { target: 800, minFieldWidth: 450, maxFieldWidth: 800, minPanelCount: 2 }

    if (cutLayout === "across")
      return config.target;

    const border = 65; // Materialzugabe Naht
    const rainCutBorder = rainCut ? 1000 * clothWidth * Math.tan(MathUtil.deg2rad(0.5)) : 0;
    const availableRollWidth = rollWidth - rainCutBorder;

    const targetPanelCount = Math.ceil(availableRollWidth / (config.target + rainCutBorder + border))
    const minPanelCount = Math.floor(availableRollWidth / (config.minFieldWidth + rainCutBorder + border))
    const panelCount = Math.max(Math.min(targetPanelCount, minPanelCount), config.minPanelCount);
    const maxLength = Math.min(Math.floor(availableRollWidth / panelCount - border - rainCutBorder), config.maxFieldWidth);
    return maxLength;
  }


  static calcMinMaxField2025(_version: ProductVersion, featureLevel: FeatureLevel, sailSize: ISize2, fieldCalculation: IRaffFieldCalculation, cutLayout: RaffFieldCutLayout, rollWidth: number, rainCut: boolean, buildTime: number): { min: number; max: number } {
    const clothLength = sailSize.h + sailSize.h * RaffLogic.getAdditionalMaterialFactorInternal(fieldCalculation, buildTime);

    const maxLength = RaffLogic.calcMaxFieldLength(sailSize.w, fieldCalculation, cutLayout, rollWidth, rainCut);

    const minFields = Math.ceil(clothLength * 1000 / maxLength);
    const allowedMinLength = featureLevel === "soliday" ? 150 : 300;
    const maxFields = clothLength * 1000 / allowedMinLength;
    return {
      min: Math.ceil(minFields),
      max: Math.floor(maxFields)
    }
  }

  static calcMinMaxField2024(_version: ProductVersion, sailSize: ISize2, fieldCalculation: IRaffFieldCalculation, cutLayout: RaffFieldCutLayout, rollWidth: number, rainCut: boolean, buildTime: number): { min: number; max: number } {
    const clothLength = sailSize.h + sailSize.h * RaffLogic.getAdditionalMaterialFactorInternal(fieldCalculation, buildTime);

    const maxLength = RaffLogic.calcMaxFieldLength(sailSize.w, fieldCalculation, cutLayout, rollWidth, rainCut);

    const minFields = Math.ceil(clothLength * 1000 / maxLength);
    const allowedMinLength = 300;
    const maxFields = clothLength * 1000 / allowedMinLength;
    return {
      min: Math.ceil(minFields),
      max: Math.floor(maxFields)
    }
  }

  static calcMinMaxFields(raff: IRaffData): { min: number; max: number } {
    return RaffLogic.calcMinMaxFieldsInternal(RaffLogic.calcSail(raff).size, raff.fieldCalculation, RaffLogic.getFieldCutLayout(raff), RaffLogic.getRollWidth(raff), RaffLogic.getRainCut(raff), raff.version, raff.featureLevel, raff.buildTime);
  }

  static calcMinMaxFieldsInternal(sailSize: ISize2, fieldCalculation: IRaffFieldCalculation, cutLayout: RaffFieldCutLayout, rollWidth: number, rainCut: boolean, version: ProductVersion, featureLevel: FeatureLevel, buildTime: number): { min: number; max: number } {
    if (Pvi.is(version, "2025")) {
      return RaffLogic.calcMinMaxField2025(version, featureLevel, sailSize, fieldCalculation, cutLayout, rollWidth, rainCut, buildTime);
    } else if (Pvi.is(version, "2024")) {
      return RaffLogic.calcMinMaxField2024(version, sailSize, fieldCalculation, cutLayout, rollWidth, rainCut, buildTime);
    }
    return RaffLogic.calcMinMaxField2022(sailSize, fieldCalculation, rollWidth, rainCut, buildTime);
  }

  static calcFieldCount(raff: IRaffData): number {
    return RaffLogic.calcFieldInfo(raff).fieldCount;
  }

  static #calcFieldInfo(raff: IRaffData): IRaffFieldInfo {
    const sail = RaffLogic.calcSail(raff)
    const { min: minFields, max: maxFields } = RaffLogic.calcMinMaxFieldsInternal(sail.size, raff.fieldCalculation, RaffLogic.getFieldCutLayout(raff), RaffLogic.getRollWidth(raff), RaffLogic.getRainCut(raff), raff.version, raff.featureLevel, raff.buildTime)

    const fieldCount = raff.fieldCalculation.type === RaffFieldCalculationType.IndividualFields ? MathUtil.clamp(raff.fieldCalculation.individualFieldCount || 0, minFields, maxFields) : minFields;
    const fieldWidth = sail.size.h / fieldCount;
    const fieldWidthCloth = (sail.size.h + sail.size.h * RaffLogic.getAdditionalMaterialFactorInternal(raff.fieldCalculation, raff.buildTime)) / fieldCount;

    return {
      fieldStart: sail.distIns[0] || 0,
      fieldCount,
      fieldWidth,
      fieldWidthCloth,
      minFields,
      maxFields
    }
  }

  static calcFieldInfo = CacheUtil.memo(RaffLogic.#calcFieldInfo, "calcFieldInfoInternal", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow"
  })

  static calcDistIns(raff: IRaffData | ISCRaffData): number[] {
    const fixed = RaffLogic.fixRaffDistIn(RaffLogic.getRaffLength(raff), raff.distIn1 || 0, raff.distIn2 || 0, raff.distInSidePriority)
    return fixed
  }

  static #calcSail(raff: IRaffData | ISCRaffData): { size: ISize2, distIns: number[] } {
    const distIns = RaffLogic.calcDistIns(raff);
    const width = RaffLogic.getRaffWidth(raff);
    const height = RaffLogic.getRaffLength(raff);
    return {
      size: {
        w: width,
        h: height - (distIns[0] || 0) - (distIns[1] || 0)
      },
      distIns
    }
  }
  static calcSail = CacheUtil.memo(RaffLogic.#calcSail, "calcSail", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow"
  })

  static calcClothSize(raff: IRaffData | ISCRaffData): ISize2 {
    const sailSize = RaffLogic.calcSail(raff).size;
    return {
      w: sailSize.w,
      h: sailSize.h + sailSize.h * RaffLogic.getAdditionalMaterialFactor(raff)
    }
  }

  static getMaxRaffDistInFactor() {
    return 0.8; // 80% => https://plaspack.atlassian.net/browse/APP22-504
  }

  static fixRaffDistIn(raffLength: number, d1: number, d2: number, distInSidePriority: number | undefined): number[] {
    const ds = d1 + d2;
    const maxVal = raffLength * RaffLogic.getMaxRaffDistInFactor();
    const delta = ds - maxVal;
    if (delta > 0) {
      if (distInSidePriority === 2) {
        let newD1 = d1 - delta;
        let newD2 = d2;
        if (newD1 < 0) {
          newD2 += newD1;
          newD1 = 0;
        }
        return [newD1, newD2];
      } else if (distInSidePriority === 1) {
        let newD1 = d1;
        let newD2 = d2 - delta;
        if (newD2 < 0) {
          newD1 += newD2;
          newD2 = 0;
        }
        return [newD1, newD2];
      } else {
        const correction = maxVal / ds
        return [d1 * correction, d2 * correction];
      }
    }

    return [d1, d2];
  }

  static getRollWidth(raff: IRaffData): number {
    return getRollWidth(raff.colorId)
  }

  static getFieldCutLayout(raff: IRaffData): RaffFieldCutLayout {
    if (ClothData.isAustronet(RaffLogic.getCloth(raff)?.clothType))
      return "across";
    return "along";

  }

  static calcGroundSize(raff: IRaffData): ISize2 {
    return {
      w: RaffLogic.getRaffWidth(raff) * Math.cos(raff.angleZ),
      h: RaffLogic.getRaffLength(raff) * Math.cos(raff.angleX),
    }
  }

  static calcProfilePinMatrices(profileMatrices: IMat4[], guides: number[], start: number = 0, skipEnd: number = 0): IMat4[] {
    const profilePinMatrices: IMat4[] = [];
    for (let iProfile = start; iProfile < profileMatrices.length - skipEnd; iProfile++) {
      const cp = profileMatrices[iProfile];
      for (let i = 0; i < guides.length; i++) {
        profilePinMatrices.push(Mat4.mul(cp, Mat4.createTranslation(guides[i], 0, 0)))
      }
    }

    return profilePinMatrices;
  }

  static calcProfileMatrices(raffType: RaffType, version: ProductVersion, raffWidth: number, raffHeight: number, angleX: number, angleZ: number, fieldPositions: number[]): IMat4[] {
    switch (raffType) {
      case RaffType.C:
        return RaffLogic.calcProfileMatricesC(raffWidth, raffHeight, angleX, angleZ, fieldPositions);
      case RaffType.M_Guide:
        return RaffLogic.calcProfileMatricesMGuide(raffWidth, raffHeight, angleX, angleZ, fieldPositions);
      case RaffType.M_Rope:
        return RaffLogic.calcProfileMatricesMRope(raffWidth, raffHeight, angleX, angleZ, Pvi.is(version, "2024") ? -0.053 : -0.068, fieldPositions);
    }
    throw new Error("Not supported so far!");
  }

  private static calcProfileMatricesC(raffWidth: number, raffHeight: number, angleX: number, angleZ: number, fieldPositions: number[]): IMat4[] {
    const topPlaneMatrix = Mat4.createTransform(0, raffHeight, 0, angleX, 0, -angleZ, 1, 1, 1);
    const dir = Vec3.transformDirection(Vec3.from(raffWidth, 0, 0), topPlaneMatrix);
    const up = Vec3.from(0, 1, 0);
    const hangOrientation = Mat4.fromBasis(dir, up, Vec3.crossNormalized(dir, up));

    const profileMatrices: IMat4[] = [];
    const carabinerHack = -0.04; // Hack => Karabiner, Aufhangeelemnt offset
    {
      const dy = -0.06 - carabinerHack;

      for (let i = 0; i < fieldPositions.length; i++) {
        const z = -fieldPositions[i];
        let hangPointMatrix: IMat4;
        if (i === 0) {
          const m = Mat4.muls(topPlaneMatrix, Mat4.createTranslation(+Math.tan(angleZ) * 0.1, -0.107, -0.045), Mat4.createRotation(-Math.PI * 0.39, 0, 0)); // Mat4.mul(Mat4.createTranslation(start), hangOrientation); // calculate new matrix with up => (0, 1, 0) => gravity...
          hangPointMatrix = m;
        } else if (i === fieldPositions.length - 1) {
          hangPointMatrix = Mat4.muls(topPlaneMatrix, Mat4.createTransform({ translation: { y: -0.048, z: z + 0.0 } }));
        } else {
          const start = Vec3.applyMatrix4(Vec3.from(0, dy, z), topPlaneMatrix);
          const m = Mat4.mul(Mat4.createTranslation(start), hangOrientation); // calculate new matrix with up => (0, 1, 0) => gravity...
          hangPointMatrix = Mat4.mul(m, Mat4.createTransform({ translation: { x: 0, y: carabinerHack } }));
        }

        profileMatrices.push(hangPointMatrix)
      }
    }
    return profileMatrices;
  }

  private static calcProfileMatricesMGuide(raffWidth: number, raffHeight: number, angleX: number, angleZ: number, fieldPositions: number[]): IMat4[] {
    const topPlaneMatrix = Mat4.createTransform(0, raffHeight, 0, angleX, 0, -angleZ, 1, 1, 1);

    const dir = Vec3.transformDirection(Vec3.from(raffWidth, 0, 0), topPlaneMatrix);
    const up = Vec3.from(0, 1, 0);
    const hangOrientation = Mat4.fromBasis(dir, up, Vec3.crossNormalized(dir, up));

    const profileMatrices: IMat4[] = [];
    const carabinerHack = -0.04; // Hack => Karabiner, Aufhangeelemnt offset
    {
      const dy = -0.06 - carabinerHack;

      for (let i = 0; i < fieldPositions.length; i++) {
        const z = -fieldPositions[i];
        const start = Vec3.applyMatrix4(Vec3.from(0, dy, z), topPlaneMatrix);

        // calculate new matrix with up => (0, 1, 0) => gravity...
        const m = Mat4.mul(Mat4.createTranslation(start), hangOrientation);

        const hangPointMatrix = Mat4.mul(m, Mat4.createTransform({ translation: { x: 0, y: carabinerHack } }));
        profileMatrices.push(hangPointMatrix)
      }
    }
    return profileMatrices;
  }

  private static calcProfileMatricesMRope(raffWidth: number, raffHeight: number, angleX: number, angleZ: number, dy: number, fieldPositions: number[]): IMat4[] {
    const topPlaneMatrix = Mat4.createTransform(0, raffHeight, 0, angleX, 0, -angleZ, 1, 1, 1);

    const dir = Vec3.transformDirection(Vec3.from(raffWidth, 0, 0), topPlaneMatrix);
    const up = Vec3.from(0, 1, 0);
    const hangOrientation = Mat4.fromBasis(dir, up, Vec3.crossNormalized(dir, up));

    const profileMatrices: IMat4[] = [];

    for (let i = 0; i < fieldPositions.length; i++) {
      const z = -fieldPositions[i];
      const start = Vec3.applyMatrix4(Vec3.from(0, 0, z), topPlaneMatrix);

      // calculate new matrix with up => (0, 1, 0) => gravity...
      const hangPointMatrix = Mat4.mul(Mat4.createTranslation(start), hangOrientation);
      profileMatrices.push(Mat4.mul(hangPointMatrix, Mat4.createTranslation(0, dy, 0)))
    }
    return profileMatrices;
  }

  public static isRaffM(raffType: RaffType): boolean {
    return raffType === RaffType.M_Guide || raffType === RaffType.M_Rope;
  }

  static calcCornerOffsets(width: number, length: number, distIn1: number = 0, distIn2: number = 0): IVec3[] {

    const offsets: IVec3[] = [
      Vec3.from(-width * 0.5, 0, length * 0.5 - distIn1),
      Vec3.from(-width * 0.5, 0, -length * 0.5 + distIn2),
      Vec3.from(width * 0.5, 0, -length * 0.5 + distIn2),
      Vec3.from(width * 0.5, 0, length * 0.5 - distIn1),
    ]
    return offsets
  }

  static calcGroundRotationMatrix(raff: IRaffData): IMat4 {
    const rotation = Mat4.createRotationY(raff.rotationY);
    return rotation;
  }

  static calcGroundMatrix(raff: IRaffData): IMat4 {
    const translation = Mat4.createTranslation(raff.position.x, raff.position.y, raff.position.z);
    const rotation = RaffLogic.calcGroundRotationMatrix(raff);
    return Mat4.muls(translation, rotation);
  }

  static calcLocalTopPlaneMatrix(raff: IRaffData): IMat4 {
    const rotationPoint = Vec3.from(-RaffLogic.getRaffWidth(raff) * 0.5, 0, RaffLogic.getRaffLength(raff) * 0.5);
    return Mat4.muls(Mat4.createTranslation(0, RaffLogic.getRaffHeight(raff), 0), Mat4.createTranslation(rotationPoint.x, rotationPoint.y, rotationPoint.z), Mat4.createRotation(raff.angleX, 0, -raff.angleZ), Mat4.createTranslation(-rotationPoint.x, -rotationPoint.y, -rotationPoint.z));
  }

  static calcTopPlaneMatrix(raff: IRaffData): IMat4 {
    const local = RaffLogic.calcLocalTopPlaneMatrix(raff)
    return Mat4.muls(RaffLogic.calcGroundMatrix(raff), local);
  }

  static calcTopPositions(raff: IRaffData): IVec3[] {
    const topPlaneMatrix = RaffLogic.calcTopPlaneMatrix(raff);
    return RaffLogic.calcCornerOffsets(RaffLogic.getRaffWidth(raff), RaffLogic.getRaffLength(raff)).map((p) => Vec3.applyMatrix4(p, topPlaneMatrix))
  }

  static #calcNetPositions(raff: IRaffData): IVec3[] {
    const topPlaneMatrix = RaffLogic.calcTopPlaneMatrix(raff);
    const distIns = RaffLogic.calcDistIns(raff);
    return RaffLogic.calcCornerOffsets(RaffLogic.getRaffWidth(raff), RaffLogic.getRaffLength(raff), distIns[0], distIns[1]).map((p) => Vec3.applyMatrix4(p, topPlaneMatrix))
  }
  static calcNetPositions = CacheUtil.memo(RaffLogic.#calcNetPositions, "calcNetPositions", {
    idsFromArgs: (raff) => [raff.id],
    resultEqualFn: "shallow"
  })

  static calcGroundPositions(raff: IRaffData): IVec3[] {
    return RaffLogic.calcTopPositions(raff).map((p) => Vec3.withY(p, raff.position.y))
  }

  static calcTopWorldPos(raff: IRaffData, offset: IVec3): IVec3 {
    const topPlaneMatrix = RaffLogic.calcTopPlaneMatrix(raff);
    return Vec3.applyMatrix4(offset, topPlaneMatrix);
  }

  static calcArea(raff: IRaffData | ISCRaffData): number {
    const size = RaffLogic.calcSail(raff).size;
    return size.w * size.h
  }

  static hasGuides(raffType: RaffType): boolean {
    return raffType === RaffType.C || raffType === RaffType.M_Guide;
  }

  static getNotch(raff: IRaffData | ISCRaffData): string | undefined {
    if (raff.raffType === RaffType.M_Guide) {
      if ("notch" in raff.articleSlots) {
        return getChecked({
          selection: raff.articleSlots?.notch,
          validItems: RaffLogic.getPossibleNotchArticles(raff),
          emptySelection: ""
        });
      } else {
        return WishList.hasArticle(raff.shoppingList.items, "00E13001773", { poleIndexFilter: -1 }) ? "00E13001773" : "";
      }
    }
    return undefined;
  }

  static getRopeTensioningSystem(raff: IRaffData | ISCRaffData): RopeTensioningSystemType | undefined {
    if (raff.raffType === RaffType.M_Rope) {
      const oldProductDefaultValue = Pvi.is(raff.version, "2024") ? "premium" : "basic"; // unset for old products: 2024, 2025 => premium, 2022 => basic
      return getChecked({
        selection: raff.ropeTensioningSystem ?? oldProductDefaultValue,
        validItems: RaffLogic.getPossibleRopeTensioningSystems(raff).possible,
        emptySelection: "__selectionRequired__"
      });
    }
    return undefined;
  }

  static getRopeMount(raff: IRaffData | ISCRaffData): string | undefined {
    if (raff.raffType === RaffType.M_Rope) {
      return getChecked({
        selection: raff.articleSlots?.ropeMount,
        validItems: RaffLogic.getPossibleRopeMounts(raff),
        emptySelection: "__selectionRequired__"
      });
    }
    return undefined;
  }

  static getRopeMountDetail(raff: IRaffData | ISCRaffData): { articleNumber: string; countPerSet: number } | undefined {
    const articleNumber =  RaffLogic.getRopeMount(raff);
    if (!articleNumber)
      return undefined;

    return {
      articleNumber,
      countPerSet: articleNumber.toUpperCase().startsWith("VPDSWAND") ? 2 : 1,
    }
  }

  static getRopeSuspension(raff: IRaffData | ISCRaffData): string | undefined {
    if (raff.raffType === RaffType.M_Rope) {
      const oldDefault = Pvi.is(raff.version, "2024") ? "00E13002056" : "00E13001224";
      return getChecked({
        selection: raff.articleSlots?.ropeSuspension ?? oldDefault,
        validItems: RaffLogic.getPossibleRopeSupsensions(raff),
        emptySelection: "__selectionRequired__"
      });
    }
    return undefined;
  }

  static getAnimationFactor(): number { // todo
    return 1.0;
  }

  static getPossibleRemoteReceiver(raffType: RaffType): string[] {
    switch (raffType) {
      case RaffType.C:
        return ["00E13001972", "00E13001924"]
    }
    return [];
  }

  static getRemoteReceiver(raff: IRaffData): string | undefined {
    return getChecked({
      selection: raff.articleSlots.remoteReceiver,
      validItems: RaffLogic.getPossibleRemoteReceiver(raff.raffType),
      emptySelection: ""
    })
  }

  static getInitialDistIn(raffType: RaffType, version: ProductVersion): { distIn1: number; distIn2: number } {
    switch (raffType) {
      case RaffType.M_Guide:
        return { distIn1: 0.05, distIn2: 0.05 }
      case RaffType.M_Rope:
        return { distIn1: Pvi.is(version, "2024") ? 0.065 : 0.05, distIn2: Pvi.is(version, "2024") ? 0.065 : 0.05 }
      case RaffType.C:
        return { distIn1: 0, distIn2: 0 }
    }
    return { distIn1: 0, distIn2: 0 }
  }

  static calcRopeLength(ropeIndex: number, raffLength: number, ropeOverrideLength: number[] | undefined): number {
    const ropeLength = ropeOverrideLength?.[ropeIndex];
    const finalLength = ropeLength || raffLength;
    return finalLength;
  }

  static getMaxShipmentLength(shipment: ShipmentType): number {
    return shipment === "post" ? 2.0 : 6.0;
  }

  static #isPreMontagePossibleBecauseOfShipment(raff: IRaffData | ISCRaffData): boolean {
    const shipment = RaffLogic.getShipment(raff);
    const maxShipmentLength = RaffLogic.getMaxShipmentLength(shipment);
    const raffWidth = RaffLogic.getRaffWidth(raff);
    return lec(raffWidth, maxShipmentLength, 2)
  }

  static isPreMontagePossible(raff: IRaffData | ISCRaffData): boolean {
    return RaffLogic.getPossiblePremontageValues(raff).length > 0;
  }

  static getPossiblePremontageValues(raff: IRaffData | ISCRaffData): string[] {
    const ret: string[] = [];
    if (RaffLogic.getSailMount(raff) === "profile") {
      if (RaffLogic.#isPreMontagePossibleBecauseOfShipment(raff)) {
        ret.push(RAFF_PREMONTAGE);
      }
    }
    return ret;
  }

  static getPreMontage(raff: IRaffData | ISCRaffData): string | undefined {
    return getChecked({
      selection: raff.articleSlots?.preMontage,
      validItems: RaffLogic.getPossiblePremontageValues(raff),
      emptySelection: undefined
    })
  }

  static calcPossiblePulleySystems(raff: IRaffData | ISCRaffData): RaffPulleySystem[] {
    if (!Pvi.is(raff.version, "2024") || ![RaffType.M_Guide, RaffType.M_Rope].includes(raff.raffType))
      return [];
    if (raff.raffType === RaffType.M_Guide || raff.raffType === RaffType.M_Rope) {
      if (raff.featureLevel === "user")
        return ["00E13002066"];
      return getKeys(raffPulleySystems);
    }
    return [];
  }

  static getPulleySystem(raff: IRaffData | ISCRaffData): RaffPulleySystem | undefined {
    return getChecked({
      selection: raff.articleSlots?.pulleySystem as RaffPulleySystem,
      validItems: RaffLogic.calcPossiblePulleySystems(raff),
      emptySelection: "" as RaffPulleySystem
    });
  }

  static _calcPulleySystemRopeLengthAuto(raff: IRaffData, pulleySystem: RaffPulleySystem): number {
    const pulleySystemInfo = raffPulleySystems[pulleySystem];
    const w = RaffLogic.getRaffWidth(raff);
    const l = RaffLogic.getRaffLength(raff)
    const h = RaffLogic.getRaffHeight(raff);
    const ropeLength = pulleySystemInfo?.type === "double" ? 4 * l + 2 * w + 2 * h : 2 * l + 1 * w + 2 * h; // https://plaspack.atlassian.net/browse/API-22
    return Math.ceil(ropeLength);
  }

  static calcPulleySystemRopeLength(raff: IRaffData): number {
    const pulleySystem = RaffLogic.getPulleySystem(raff)
    if (!pulleySystem)
      return 0;
    return raff.pulleySystemRopeLength || RaffLogic._calcPulleySystemRopeLengthAuto(raff, pulleySystem);
  }

  static calcPossiblePulleySystemRopes(raff: IRaffData): string[] {
    const pulleySystem = RaffLogic.getPulleySystem(raff);
    if (!pulleySystem)
      return [];
    return RopeHelper.getRopeArticles(["4", "6.5"]).map((r) => r.id)
  }

  static getPossibleRollTypes(raff: ISCRaffData | IRaffData): RaffRollType[] {
    if (raff.raffType === RaffType.M_Guide)
      return ["metal", "plastic"];
    return [];
  }

  static getPulleySystemRope(raff: IRaffData): string | undefined {
    return getChecked({
      selection: raff.articleSlots?.pulleySystemRope,
      validItems: RaffLogic.calcPossiblePulleySystemRopes(raff),
      emptySelection: undefined
    });
  }

  static getRollType(raff: ISCRaffData | IRaffData): RaffRollType {
    return getChecked({
      selection: raff?.rollType,
      validItems: RaffLogic.getPossibleRollTypes(raff),
      emptySelection: "__selectionRequired__"
    })!;
  }

  /* static getFieldCalculation(product: ISCRaffData | IRaffData): RaffFieldCalculationType {
     return product.fieldCalculation?.type || RaffFieldCalculationType.Standard;
   }*/

  static getPossibleShipments(raff: IRaffData | ISCRaffData): ShipmentType[] {
    if ([RaffType.M_Rope, RaffType.M_Guide].includes(raff.raffType))
      return ["spedition", "post"];
    if (lec(raff.width, 2) && lec(raff.length, 2))
      return ["post"];
    return ["spedition"];
  }

  static getShipment(raff: IRaffData | ISCRaffData): ShipmentType {
    return getChecked({
      selection: raff?.shipment,
      validItems: RaffLogic.getPossibleShipments(raff),
      emptySelection: "__selectionRequired__"
    })!;
  }

  static getPossibleNotchArticles(raff: IRaffData | ISCRaffData): string[] {
    if (raff.raffType === RaffType.M_Guide)
      return ["00E13001773"]
    return [];
  }

  static getPossibleRopeTensioningSystems(raff: IRaffData | ISCRaffData): { possible: RopeTensioningSystemType[], displayed: RopeTensioningSystemType[], disabled: RopeTensioningSystemType[] } {
    const result: { possible: RopeTensioningSystemType[], displayed: RopeTensioningSystemType[], disabled: RopeTensioningSystemType[] } = { possible: [], disabled: [], displayed: [] };

    if (raff.raffType === RaffType.M_Rope) {
      const allRopeTensioningSystems: RopeTensioningSystemType[] = ["basic", "premium", "none"];
      //const raffSizeWithoutTensioning = RaffLogic.getRaffSize(raff, { skipTensioningSystem: true });

      for (const ropeTensioningSystem of allRopeTensioningSystems) {
        result.displayed.push(ropeTensioningSystem);
        //if (Size2.leq(raffSizeWithoutTensioning, RaffLogic.calcMaxRopeTensioningSize(ropeTensioningSystem, raff.featureLevel)))
          result.possible.push(ropeTensioningSystem);
        //else
        //  result.disabled.push(ropeTensioningSystem);
      }
      return result;
    }
    return result;
  }

  static getPossibleRopeMounts(raff: IRaffData | ISCRaffData): string[] {
    if (raff.raffType === RaffType.M_Rope) {
      const ropeTensioningSystem = RaffLogic.getRopeTensioningSystem(raff);
      if (ropeTensioningSystem === "basic") {
        const articles: string[] = [];
        const material = RaffLogic.getPergolaMaterial(raff);
        if (material === "wood") {
          articles.push("00E13000063");
        } else if (material === "metal") {
          articles.push("00E13000127");
          articles.push("00E13000066");
        }
        articles.push("00E13000234");
        return articles;
      }
      else if (ropeTensioningSystem === "premium") {
        if (raff.featureLevel === "user")
          return ["VPDSWAND01"]
        const ret: string[] = [];
        ret.push("VPDSWAND01");
        if (lec(RaffLogic.getRaffLength(raff), 9, 3))
          ret.push("VPDSWAND02");
        ret.push("VPDSWAND03");
        return ret;
      }
    }
    return [];
  }

  static getPossibleRopeSupsensions(raff: IRaffData | ISCRaffData): string[] {
    if (raff.raffType === RaffType.M_Rope)
      if (RaffLogic.getSailMount(raff) === "profile") {
        return ["00E13001372", "00E13002056"];
      }
      else {
        return ["00E13001224", "00E13001952"];
      }
    return [];
  }
}